import type { IEpicBreakdownRow } from '@skystone/shared';

interface IEpicBreakdownStackedProps {
  rows: readonly IEpicBreakdownRow[];
}

function MetricRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground tabular-nums">{value}</span>
    </div>
  );
}

function trendLabel(direction: IEpicBreakdownRow['trendDirection']): string {
  if (direction === 'up') return '↑ worsening';
  if (direction === 'down') return '↓ improving';
  if (direction === 'flat') return '→ flat';
  return '—';
}

function fmtNumOrDash(value: number | null, unit = ''): string {
  return value === null ? '—' : `${value}${unit}`;
}

/**
 * Narrow-width fallback for the Per-Epic Breakdown table. Each epic becomes
 * a small card with the name + product + key on top and the five metrics
 * stacked below. No sorting UX at this width — that's a table-mode affordance.
 */
export function EpicBreakdownStacked({ rows }: Readonly<IEpicBreakdownStackedProps>) {
  if (rows.length === 0) return null;
  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.epicKey} className="space-y-2 rounded-md border border-border p-3">
          <div className="space-y-0.5">
            <div className="text-sm font-semibold text-foreground">{r.epicName}</div>
            <div className="text-[11px] text-muted-foreground">
              {r.productName} · {r.epicKey}
            </div>
          </div>
          <div className="space-y-1">
            <MetricRow
              label="Completion"
              value={r.total === 0 ? '— no issues —' : `${r.done}/${r.total} · ${Math.round(r.completionRate)}%`}
            />
            <MetricRow
              label="Cycle (median)"
              value={fmtNumOrDash(r.cycleMedianDays === null ? null : Math.round(r.cycleMedianDays * 10) / 10, 'd')}
            />
            <MetricRow
              label="Throughput"
              value={fmtNumOrDash(r.throughputPerWeek === null ? null : Math.round(r.throughputPerWeek * 10) / 10, ' / wk')}
            />
            <MetricRow label="WIP" value={String(r.wipCount)} />
            <MetricRow label="Trend" value={trendLabel(r.trendDirection)} />
          </div>
        </li>
      ))}
    </ul>
  );
}
